// Capability Setup: what a team manager defines before anything can be
// assessed or trained. Per team, its roles; per role, its capabilities; per
// capability, the criteria for each of the four levels. Assessments and
// Trainings then pick the capabilities they cover from what is set up here, and
// every Roleplay on a capability is graded against that capability's levels.
//
// A role is set up in two confirmations. The manager lists its capabilities and
// confirms them; that is when Elliott drafts the four levels for each (the
// backend, POST /v3/admin/capability-setup/draft-levels). They review and edit
// the drafts, then confirm the levels. Where Elliott can't draft (switched off,
// or the call failed), the levels start empty and the manager writes them. A
// role with no capabilities of its own, only the org admin's shared ones
// applied to it (./sharedSets.ts), is confirmed in one step: confirming its
// capabilities confirms its levels too, since it has none of its own to write.
//
// Readiness is sticky. A role is ready once its manager has confirmed it, and
// stays ready when its team adds capabilities later: one added since counts
// (in scores, Assessments, Trainings, learner views) once its four levels are
// written and the role's levels are confirmed again. Until then the role is
// measured on what was confirmed (confirmedCapabilityIds) and on the shared
// capabilities applied to it. Once confirmed, a role can't be deleted.
//
// The backend keeps the setup and applies the same rules (BE
// shared/services/capabilitySetup/rules.ts); its answer is the truth. The
// limits below mirror its own, so the page can refuse what it would refuse.

import { LEVEL_ORDER, type CapabilityLevel } from '../Program/components/learning-lifecycle/teamCapability';
import { creationFlowsReady } from './flags';
import type { CapabilitySource } from './capabilitySource';

export type LevelCriteria = Partial<Record<CapabilityLevel, string>>;

export interface SetupCapability {
  id: string;
  name: string;
  /** What a response at each level looks like. A level with no text is not set yet. */
  criteria: LevelCriteria;
  /** The shared capability it was made from, when its levels or name were changed on this role (the backend sets it). */
  forkedFrom?: string;
  /**
   * Where it comes from: the role's own (absent, or 'role'), or one of the org
   * admin's shared sets applied to the role (see ./sharedSets.ts).
   */
  source?: CapabilitySource;
  /** For a shared one: the org admin who last applied or changed it. */
  setBy?: { id: string; name: string } | null;
  /** In a shared set: the roles it applies to (by id). */
  roleIds?: string[];
  /** In a shared set: it applies to every role, those added later too. */
  allRoles?: boolean;
}

export interface SetupRole {
  id: string;
  teamId: string;
  name: string;
  capabilities: SetupCapability[];
  /** The manager has confirmed this role's list of capabilities (Elliott then drafts their levels). */
  capabilitiesConfirmed?: boolean;
  /** The manager has reviewed and confirmed the levels of every capability. */
  levelsConfirmed?: boolean;
  /**
   * It has been confirmed at least once (its levels; with only shared
   * capabilities, its capabilities). From then on the role can be renamed but
   * not deleted, and it stays ready: Assessments, Trainings and scores may hang off it.
   */
  established?: boolean;
  /**
   * The capabilities of its own that were on it when its levels were last
   * confirmed (the backend keeps them). While its levels are reopened for one
   * added since, only these count. Absent before the backend kept them.
   */
  confirmedCapabilityIds?: string[];
  /** An Assessment has been run or a Training published for this role. */
  launched?: boolean;
  /**
   * The backend's word on whether it is ready to assess, train on and invite to
   * (R4: confirmed, with something counting on it, the shared capabilities
   * applied to it included). Absent from a role the backend has not answered for.
   */
  ready?: boolean;
  /**
   * Holds as many capabilities as it is given: one of the org admin's shared
   * sets, edited as a role is (./sharedSets.ts). A team's role holds MAX_CAPABILITIES.
   */
  unlimited?: boolean;
  /**
   * The backend's version of the role: 0 when added, one more with every saved
   * change. A change names the version it was made from, so one made from an
   * out-of-date role is refused rather than written over another. Absent in the
   * browser-only fallback.
   */
  version?: number;
}

/** A team of the org's Teams list, as Capability Setup shows it. */
export interface SetupTeam {
  id: string;
  name: string;
  /** Whether the viewer may set it up: org admins any team, team managers the teams they manage. */
  canEdit: boolean;
  /** Its assigned team managers (org admins manage every team and are not listed). */
  managers: { id: string; name: string }[];
  /** Someone has been added or invited to it, or it was confirmed in Operations: the Invite Team step is done. */
  invited?: boolean;
}

/** The backend's limits on a role. */
export const MAX_NAME = 80;
export const MAX_CRITERIA = 2000;
/** A team's role: its own capabilities. The shared ones applied to it come on top, with no limit. */
export const MAX_CAPABILITIES = 25;

/** How many capabilities a role (or a shared set) can hold. */
export const capacityOf = (role: Pick<SetupRole, 'unlimited'>): number =>
  role.unlimited ? Number.POSITIVE_INFINITY : MAX_CAPABILITIES;

export type SetupStepKey = 'roles' | 'capabilities' | 'levels' | 'launch' | 'invite';
/** `current` = open and in progress (brandy); `done` = green; `locked` = waits on an earlier step. */
export type SetupStepStatus = 'done' | 'current' | 'locked';

export interface SetupStep {
  key: SetupStepKey;
  title: string;
  description: string;
  status: SetupStepStatus;
  /** Open to do, but nothing waits on it. */
  optional?: boolean;
  /** Its detail is a running count worth showing whatever its status ("1/2 Roles Confirmed"). */
  showDetail?: boolean;
  /** Where it stands, in a few words ("2 Roles", "1 of 2 Roles"). */
  detail: string;
}

/** Drafts the four levels for a capability (Elliott). */
export type LevelDrafter = (capabilityName: string) => LevelCriteria;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const tidy = (text: string) => text.trim().replace(/\s+/g, ' ');
/**
 * "account executive" reads as "Account Executive", "éclair lead" as "Éclair Lead".
 * Capitals already there are kept ("SDR"). The backend capitalizes the same way.
 */
export const titleCase = (text: string) =>
  text.replace(/(^|[\s\-/&(])(\p{Ll})/gu, (_m, lead: string, letter: string) => lead + letter.toUpperCase());

/** How many of a capability's four levels have criteria. */
export const levelsSet = (capability: SetupCapability): number =>
  LEVEL_ORDER.filter((level) => Boolean(capability.criteria[level]?.trim())).length;

/** A capability is ready once all four levels have criteria. */
export const hasAllLevels = (capability: SetupCapability): boolean => levelsSet(capability) === LEVEL_ORDER.length;

/** A role's levels can be confirmed once its capabilities are confirmed and every one has all four levels. */
export const canConfirmLevels = (role: SetupRole): boolean =>
  Boolean(role.capabilitiesConfirmed) && role.capabilities.length > 0 && role.capabilities.every(hasAllLevels);

/**
 * A role's capabilities can be confirmed once it has some: its own, or shared
 * ones applied to it (`applied`, how many).
 */
export const canConfirmCapabilities = (role: SetupRole, applied = 0): boolean =>
  role.capabilities.length > 0 || applied > 0;

/** Its capabilities and their levels are confirmed as they stand now. */
export const levelsDone = (role: SetupRole | null): boolean =>
  Boolean(role?.capabilitiesConfirmed && role.levelsConfirmed);

/**
 * Its manager has confirmed it at least once (the backend's `established`): it
 * stays set up from then on, and can't be deleted.
 */
export const isSettled = (role: SetupRole | null): boolean => Boolean(role?.established) || levelsDone(role);

/** The ids of its own capabilities confirmed when its levels were last confirmed: every one while they are. */
export const confirmedIdsOf = (role: SetupRole): string[] =>
  levelsDone(role) ? role.capabilities.map((c) => c.id) : role.confirmedCapabilityIds ?? [];

/**
 * The role's own capabilities that count (the backend's countedCapabilities):
 * none before it is first confirmed; every one while its levels are confirmed;
 * while they are reopened for one added since, those confirmed last time that
 * still have all four levels.
 */
export const countedCapabilities = (role: SetupRole): SetupCapability[] => {
  if (!isSettled(role)) return [];
  const kept = new Set(confirmedIdsOf(role));
  return role.capabilities.filter((c) => hasAllLevels(c) && kept.has(c.id));
};

/** The role as Assessments and Trainings read it: only the capabilities of its own that count. */
export const withCountedCapabilities = (role: SetupRole): SetupRole => ({
  ...role,
  capabilities: countedCapabilities(role)
});

/**
 * A role is ready to assess, train on and invite to once its manager has
 * confirmed it and something counts on it: a confirmed capability of its own,
 * or a shared one applied to it (`applied`, how many). It stays ready while
 * its team adds capabilities: those wait for the levels to be confirmed again.
 */
export const isRoleReady = (role: SetupRole | null, applied = 0): boolean =>
  Boolean(role) && isSettled(role) && countedCapabilities(role as SetupRole).length + applied > 0;

export interface SetupStepsOptions {
  /** The team has been invited (step 5 is done). */
  invited?: boolean;
  /** How many shared capabilities apply to a role: with none of its own, a role is confirmed on those. */
  applied?: (role: SetupRole) => number;
  /** Run Assessment, Publish Training and Invite Team exist (see flags.ts), so steps 4 and 5 are there. */
  creationFlows?: boolean;
  /** Elliott drafts the levels: step 3 is reviewing them rather than writing them. */
  elliott?: boolean;
}

/**
 * The checklist for one TEAM, across all of its roles:
 *  1. Add Roles             done with at least one role.
 *  2. Confirm Capabilities  done when every role's capabilities are confirmed.
 *  3. Confirm Levels        open as soon as one role's capabilities are confirmed (so it
 *                           can be in progress alongside step 2, role by role); done
 *                           when every role's levels are confirmed.
 *  4. Assess or Train       optional: open (current) once levels are done, and never holds up step 5.
 *  5. Invite Team           opens once levels are done.
 * A role with only shared capabilities applied counts for steps 2 and 3 once
 * its capabilities are confirmed (that confirms its levels). Steps 1 to 3 open
 * one after another. Steps 4 and 5 both open once levels are done, and stay
 * open while a role that was set up waits for the levels of a capability
 * added since (it is still ready), so the first open step is the one the team is on.
 * Where Run Assessment, Publish Training and Invite Team are not built yet
 * (production), the checklist is steps 1 to 3: the team is set up once every
 * role's levels are confirmed.
 */
export const setupSteps = (
  teamRoles: SetupRole[],
  { invited = false, creationFlows = creationFlowsReady(), elliott = true, applied = () => 0 }: SetupStepsOptions = {}
): SetupStep[] => {
  const withCapabilities = teamRoles.filter(
    (r) => r.capabilitiesConfirmed && canConfirmCapabilities(r, applied(r))
  ).length;
  const withLevels = teamRoles.filter((r) => levelsDone(r) && canConfirmCapabilities(r, applied(r))).length;
  const rolesDone = teamRoles.length > 0;
  const capabilitiesDone = rolesDone && withCapabilities === teamRoles.length;
  const allLevelsDone = capabilitiesDone && withLevels === teamRoles.length;
  // Every role set up: ready, a capability added since or not.
  const allReady = rolesDone && teamRoles.every((r) => isRoleReady(r, applied(r)));
  const opened = allLevelsDone || allReady;
  const launched = teamRoles.some((r) => r.launched);
  const ofRoles = (n: number) => `${n}/${teamRoles.length} roles confirmed`;

  const required = (done: boolean, unlocked: boolean): SetupStepStatus =>
    done ? 'done' : unlocked ? 'current' : 'locked';

  const setUp: SetupStep[] = [
    {
      key: 'roles',
      title: 'Add roles',
      description: 'The roles people hold in this team.',
      status: required(rolesDone, true),
      detail: plural(teamRoles.length, 'role', 'roles')
    },
    {
      key: 'capabilities',
      title: 'Confirm capabilities',
      description: 'List what each role needs to be able to do, then confirm the list for every role.',
      status: required(capabilitiesDone, rolesDone),
      detail: ofRoles(withCapabilities),
      showDetail: true
    },
    {
      key: 'levels',
      title: 'Confirm levels',
      description: elliott
        ? 'Elliott drafts what Weak, Developing, Capable and Advanced look like for each capability. Review, edit and confirm them for every role.'
        : 'Write what Weak, Developing, Capable and Advanced look like for each capability, then confirm them for every role.',
      status: required(allLevelsDone, withCapabilities > 0),
      detail: ofRoles(withLevels),
      showDetail: true
    }
  ];
  if (!creationFlows) return setUp;
  return [
    ...setUp,
    {
      key: 'launch',
      title: 'Assess or train',
      description: 'Optional. Run an Assessment or publish a Training on the capabilities set up here.',
      status: required(launched, opened),
      optional: true,
      detail: launched ? 'Published' : 'Optional'
    },
    {
      key: 'invite',
      title: 'Invite team',
      description: 'See who from this team is already on Dosen, then invite the rest.',
      status: required(invited, opened),
      detail: invited ? 'Invited' : 'Not invited'
    }
  ];
};

/** A new role for a team, or null when the name is blank or already taken in that team. */
export const newRole = (roles: SetupRole[], teamId: string, name: string): SetupRole | null => {
  const trimmed = titleCase(tidy(name));
  if (!trimmed) return null;
  const taken = roles.some((r) => r.teamId === teamId && r.name.toLowerCase() === trimmed.toLowerCase());
  if (taken) return null;
  return { id: `${teamId}:${Date.now().toString(36)}`, teamId, name: trimmed, capabilities: [] };
};

let capabilitySeq = 0;

/** Two names that read the same, whatever their case and spacing. */
const sameName = (a: string, b: string) => tidy(a).toLowerCase() === tidy(b).toLowerCase();

/**
 * The role with a capability added, or null when the name is blank, already on
 * the role, or the role has as many capabilities as it can hold. Added after the
 * list was confirmed, it gets its draft levels straight away and the role's
 * levels need confirming again.
 */
export const addCapability = (role: SetupRole, name: string, draft?: LevelDrafter): SetupRole | null => {
  const trimmed = tidy(name);
  if (!trimmed || role.capabilities.length >= capacityOf(role)) return null;
  if (role.capabilities.some((c) => sameName(c.name, trimmed))) return null;
  capabilitySeq += 1;
  const capability: SetupCapability = {
    id: `${role.id}:c${Date.now().toString(36)}${capabilitySeq}`,
    name: trimmed,
    criteria: role.capabilitiesConfirmed && draft ? draft(trimmed) : {}
  };
  return { ...role, capabilities: [...role.capabilities, capability], levelsConfirmed: false };
};

/** A capability picked to import to a role: its id, name and levels, as the role it comes from has them. */
export interface ImportChoice {
  /** The capability's own id: the backend keeps it, and the two roles share the one capability. */
  id?: string;
  name: string;
  criteria: LevelCriteria;
}

/**
 * The role with capabilities imported from other roles of its team. Each
 * arrives as it is, id, name and levels: the same capability on both roles
 * (see ./sharedCapabilities.ts). The role's levels then need confirming again,
 * so the manager has seen them for this role. Null when nothing could be added
 * (no room, or every one already on the role).
 */
export const importCapabilities = (role: SetupRole, choices: ImportChoice[]): SetupRole | null => {
  const added: SetupCapability[] = [];
  choices.forEach((choice) => {
    const name = tidy(choice.name);
    if (!name || role.capabilities.length + added.length >= capacityOf(role)) return;
    if ([...role.capabilities, ...added].some((c) => sameName(c.name, name) || (choice.id && c.id === choice.id))) {
      return;
    }
    capabilitySeq += 1;
    added.push({
      id: choice.id ?? `${role.id}:c${Date.now().toString(36)}${capabilitySeq}`,
      name: name.slice(0, MAX_NAME),
      criteria: { ...choice.criteria }
    });
  });
  if (added.length === 0) return null;
  return { ...role, capabilities: [...role.capabilities, ...added], levelsConfirmed: false };
};

/**
 * The role with a capability's name changed, as it is typed: a name passes
 * through blank or repeated on its way to the next one, so nothing is refused
 * here. capabilityNameProblem says when the name can't be saved, and the page
 * holds Save Changes until it can.
 */
export const renameCapability = (role: SetupRole, capabilityId: string, name: string): SetupRole => ({
  ...role,
  capabilities: role.capabilities.map((c) => (c.id === capabilityId ? { ...c, name: name.slice(0, MAX_NAME) } : c))
});

/** Why a capability's name can't be saved (blank, too long, or another capability's), or null when it can. */
export const capabilityNameProblem = (role: SetupRole, capabilityId: string): string | null => {
  const index = role.capabilities.findIndex((c) => c.id === capabilityId);
  if (index < 0) return null;
  const name = tidy(role.capabilities[index].name);
  if (!name) return `Capability ${index + 1} needs a name.`;
  if (name.length > MAX_NAME) return `A capability's name can be at most ${MAX_NAME} characters.`;
  const repeated = role.capabilities.some((c, i) => i !== index && sameName(c.name, name));
  return repeated ? `${role.name} has two capabilities called ${name}.` : null;
};

/**
 * Why the role can't be saved as it stands, or null when it can: the
 * backend's rules, checked here first so the page can say why instead of
 * sending what would be refused.
 *
 * `base` is the role as last saved. Only growth past the limit is refused: a
 * role saved with more capabilities than a team's role may now hold (the limit
 * came down from 40 to 25) can still be renamed and edited, and can shrink.
 */
export const roleProblem = (role: SetupRole, base?: Pick<SetupRole, 'capabilities'> | null): string | null => {
  const held = base?.capabilities.length ?? 0;
  if (role.capabilities.length > capacityOf(role) && role.capabilities.length > held) {
    return `A role can have at most ${MAX_CAPABILITIES} capabilities.`;
  }
  const named = role.capabilities.map((c) => capabilityNameProblem(role, c.id)).find(Boolean);
  if (named) return named;
  const tooLong = role.capabilities.some((c) =>
    LEVEL_ORDER.some((level) => (c.criteria[level] ?? '').trim().length > MAX_CRITERIA)
  );
  return tooLong ? `A level description can be up to ${MAX_CRITERIA} characters.` : null;
};

/** The role without that capability. With none left, there is nothing confirmed any more. */
export const removeCapability = (role: SetupRole, capabilityId: string): SetupRole => {
  const capabilities = role.capabilities.filter((c) => c.id !== capabilityId);
  return capabilities.length > 0
    ? { ...role, capabilities }
    : { ...role, capabilities, capabilitiesConfirmed: false, levelsConfirmed: false };
};

/** Sets one level's criteria. Clearing a level leaves the role's levels unconfirmed. */
export const setCriteria = (
  role: SetupRole,
  capabilityId: string,
  level: CapabilityLevel,
  text: string
): SetupRole => ({
  ...role,
  capabilities: role.capabilities.map((c) =>
    c.id === capabilityId ? { ...c, criteria: { ...c.criteria, [level]: text } } : c
  ),
  levelsConfirmed: text.trim() ? role.levelsConfirmed : false
});

/**
 * Confirms the role's capabilities, which is when Elliott drafts their levels:
 * every level still blank is filled from the draft. Needs at least one
 * capability: of its own, or shared ones applied to it (`applied`, how many).
 * A role with only shared ones has no levels of its own to write: confirming
 * its capabilities confirms its levels, and it is set up (the backend does the
 * same, and it can no longer be deleted).
 */
export const confirmCapabilities = (role: SetupRole, draft?: LevelDrafter, applied = 0): SetupRole => {
  if (role.capabilities.length === 0) {
    return applied > 0
      ? { ...role, capabilitiesConfirmed: true, levelsConfirmed: true, established: true, confirmedCapabilityIds: [] }
      : role;
  }
  return {
    ...role,
    capabilitiesConfirmed: true,
    capabilities: role.capabilities.map((c) => {
      const drafted = draft ? draft(c.name) : {};
      const criteria: LevelCriteria = {};
      LEVEL_ORDER.forEach((level) => {
        criteria[level] = c.criteria[level]?.trim() ? c.criteria[level] : drafted[level] ?? '';
      });
      return { ...c, criteria };
    })
  };
};

/**
 * The role with Elliott's levels for one capability, in every level still blank
 * (what the manager wrote stays); null when the role no longer has it.
 */
export const fillLevels = (role: SetupRole, capabilityName: string, drafted: LevelCriteria): SetupRole | null => {
  if (!role.capabilities.some((c) => sameName(c.name, capabilityName))) return null;
  return {
    ...role,
    capabilities: role.capabilities.map((c) => {
      if (!sameName(c.name, capabilityName)) return c;
      const criteria: LevelCriteria = { ...c.criteria };
      LEVEL_ORDER.forEach((level) => {
        if (!criteria[level]?.trim() && drafted[level]?.trim()) criteria[level] = drafted[level];
      });
      return { ...c, criteria };
    })
  };
};

/** Confirms the role's levels, if every capability has all four: every one of them counts from then on. */
export const confirmLevels = (role: SetupRole): SetupRole =>
  canConfirmLevels(role)
    ? { ...role, levelsConfirmed: true, established: true, confirmedCapabilityIds: role.capabilities.map((c) => c.id) }
    : role;

/**
 * A role can be deleted until it is first confirmed (its levels; with only
 * shared capabilities, its capabilities), and never once launched.
 */
export const canDeleteRole = (role: SetupRole): boolean => !isSettled(role) && !role.launched;

/**
 * DUMMY, the browser-only fallback: the role after a change, its confirmations
 * kept as the backend keeps them (rules.ts applyRoleInput there): confirmed
 * once is confirmed for good, and the capabilities confirmed last time are
 * kept while the levels are reopened. In `api` mode the backend's answer is
 * the truth instead.
 */
export const settleLocally = (saved: SetupRole, next: SetupRole): SetupRole => {
  const confirmed = levelsDone(next);
  const was = confirmedIdsOf(saved);
  return {
    ...next,
    established: Boolean(saved.established || next.established || confirmed),
    confirmedCapabilityIds: confirmed
      ? next.capabilities.map((c) => c.id)
      : was.filter((id) => next.capabilities.some((c) => c.id === id))
  };
};

/** The roles with one renamed, or null when the name is blank or taken by another role in its team. */
export const renameRole = (roles: SetupRole[], roleId: string, name: string): SetupRole[] | null => {
  const trimmed = titleCase(tidy(name));
  const role = roles.find((r) => r.id === roleId);
  if (!role || !trimmed) return null;
  const taken = roles.some(
    (r) => r.id !== roleId && r.teamId === role.teamId && r.name.toLowerCase() === trimmed.toLowerCase()
  );
  return taken ? null : roles.map((r) => (r.id === roleId ? { ...r, name: trimmed } : r));
};

/** The roles without that one, unless it is past the point of deleting. */
export const deleteRole = (roles: SetupRole[], roleId: string): SetupRole[] =>
  roles.filter((r) => r.id !== roleId || !canDeleteRole(r));

/**
 * The role a draft's Save Changes asks for, made from the role as last saved:
 * the draft's capabilities (they replace the list), and the confirmations as
 * saved, except one the draft's edits turned off (a capability added, a level
 * cleared, the last capability removed). `base` is the saved role the draft
 * started from, so a confirmation that changed on the saved role since (another
 * manager, a reload after a refused save) is neither undone nor brought back.
 */
export const applyDraft = (latest: SetupRole, base: SetupRole, draft: SetupRole): SetupRole => {
  const turnedOff = (key: 'capabilitiesConfirmed' | 'levelsConfirmed') => Boolean(base[key]) && !draft[key];
  return {
    ...latest,
    capabilities: draft.capabilities,
    capabilitiesConfirmed: turnedOff('capabilitiesConfirmed') ? false : latest.capabilitiesConfirmed,
    levelsConfirmed: turnedOff('levelsConfirmed') ? false : latest.levelsConfirmed
  };
};

/** What a save sends besides the version: only what changed. `capabilities`, when sent, replaces the list. */
export interface RoleUpdate {
  name?: string;
  capabilities?: SetupCapability[];
  capabilitiesConfirmed?: boolean;
  levelsConfirmed?: boolean;
  launched?: boolean;
}

/** A role's capabilities as they are sent, each with all four levels. */
const sentCapabilities = (role: SetupRole): SetupCapability[] =>
  role.capabilities.map((c) => {
    const criteria: LevelCriteria = {};
    LEVEL_ORDER.forEach((level) => {
      criteria[level] = c.criteria[level] ?? '';
    });
    return { id: c.id, name: c.name, criteria };
  });

const CONFIRMATIONS = ['capabilitiesConfirmed', 'levelsConfirmed', 'launched'] as const;

/**
 * The fields that differ between the saved role and the role as it should be,
 * or null when nothing does. `established` is never sent: the backend sets it.
 */
export const roleUpdate = (saved: SetupRole, next: SetupRole): RoleUpdate | null => {
  const update: RoleUpdate = {};
  if (next.name !== saved.name) update.name = next.name;
  const capabilities = sentCapabilities(next);
  if (JSON.stringify(capabilities) !== JSON.stringify(sentCapabilities(saved))) update.capabilities = capabilities;
  CONFIRMATIONS.forEach((key) => {
    if (Boolean(next[key]) !== Boolean(saved[key])) update[key] = Boolean(next[key]);
  });
  return Object.keys(update).length > 0 ? update : null;
};

/**
 * The roles after a reload in the background, kept in step with what was saved
 * here while it was on its way: a role a save has answered since keeps that
 * answer (versions only go up), a role deleted here stays gone, and a role added
 * here after the reload was asked for stays.
 */
export const mergeLoadedRoles = (
  current: SetupRole[],
  loaded: SetupRole[],
  { deleted, added }: { deleted: ReadonlySet<string>; added: ReadonlySet<string> }
): SetupRole[] => {
  const here = new Map(current.map((r) => [r.id, r]));
  const merged = loaded
    .filter((r) => !deleted.has(r.id))
    .map((r) => {
      const mine = here.get(r.id);
      return mine && (mine.version ?? 0) > (r.version ?? 0) ? mine : r;
    });
  const answered = new Set(merged.map((r) => r.id));
  return [...merged, ...current.filter((r) => added.has(r.id) && !answered.has(r.id))];
};

// DUMMY, development only: the fallback for an API without the Capability
// Setup endpoints (see useCapabilityRoles), where the setup lives in this
// browser, per organization. Remove with that fallback.
const storageKey = (orgId: string) => `dosen.capabilitySetup.${orgId || 'org'}`;

export const loadSetup = (orgId: string): SetupRole[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(orgId)) ?? '[]');
    // Roles saved before names were capitalized read the same as new ones.
    return Array.isArray(parsed) ? parsed.map((r: SetupRole) => ({ ...r, name: titleCase(r.name ?? '') })) : [];
  } catch {
    return [];
  }
};

export const saveSetup = (orgId: string, roles: SetupRole[]): void => {
  try {
    localStorage.setItem(storageKey(orgId), JSON.stringify(roles));
  } catch {
    // Storage full or blocked: the setup still holds for this visit.
  }
};
